import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { IEventProperties } from '../../services/analytics/analytics.service';

export interface IAnalyticsBoundaryData {
    name: string;
    properties: IEventProperties;
}

export interface IAnalyticsSection {
    getBoundariesFullData?(): IAnalyticsBoundaryData;
}

interface Props {
    name: string;
    properties?: IEventProperties;
    children: React.ReactNode[] | React.ReactNode;
}

export const AnalyticsBoundaryContext = createContext<IAnalyticsSection>({});

export const AnalyticsBoundaryProvider = ({
    name,
    properties,
    children
}: Props) => {
    const upperAnalyticsBoundary = useContext(AnalyticsBoundaryContext);

    const getBoundariesFullData = useCallback(() => {
        if (upperAnalyticsBoundary.getBoundariesFullData) {
            const upperAnalyticsBoundariesData =
                upperAnalyticsBoundary.getBoundariesFullData();

            // handle the case where the upper boundary has no name
            // so it won't be something like 'upperName/undefined/childName'
            const concatenatedName = [upperAnalyticsBoundariesData.name, name]
                .filter(Boolean)
                .join('/');

            return {
                name: concatenatedName,
                properties: Object.assign(
                    upperAnalyticsBoundariesData.properties,
                    properties || {}
                )
            };
        }

        return {
            name,
            properties: { ...(properties || {}) }
        };
    }, [name, properties, upperAnalyticsBoundary]);

    const value = useMemo(
        () => ({ getBoundariesFullData }),
        [getBoundariesFullData]
    );

    return (
        <AnalyticsBoundaryContext.Provider value={value}>
            {children}
        </AnalyticsBoundaryContext.Provider>
    );
};
